import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../api/client'
import type { CustomerDetailOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Kpi } from '../components/Kpi'
import { Pager } from '../components/Pager'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { usePaging } from '../lib/filters'
import { count, formatDate, money, moneyCompact, monthLabel, num } from '../lib/format'

export function CustomerDetailPage() {
  const { customerId = '' } = useParams()
  const { offset, limit, setOffset } = usePaging(20)

  const query = useQuery({
    queryKey: ['customer', customerId, offset, limit],
    queryFn: () =>
      api.get<CustomerDetailOut>(`/analytics/customers/${encodeURIComponent(customerId)}`, {
        limit,
        offset,
      }),
  })

  const customer = query.data
  const series = (customer?.monthly_series ?? []).map((point) => ({
    label: monthLabel(point.month),
    total: num(point.total),
    orders: point.order_count,
  }))

  return (
    <>
      <PageHeader title={customer?.name ?? 'Customer'} subtitle={customer?.phone ?? undefined}>
        <Link className="btn" to="/customers">
          ← All customers
        </Link>
      </PageHeader>

      <div className="content stack">
        <Panel isLoading={query.isPending} error={query.error}>
          {customer ? (
            <>
              {customer.linked_profiles > 1 ? (
                <div className="alert info">
                  This person has <strong>{customer.linked_profiles}</strong> linked customer profiles
                  in HIKE; the figures below combine them.
                </div>
              ) : null}

              <div className="grid cols-4">
                <Kpi label="Total spend" value={money(customer.total_spend)} />
                <Kpi label="Orders" value={count(customer.order_count)} />
                <Kpi label="Average order" value={money(customer.avg_order_value)} />
                <Kpi label="Layby outstanding" value={money(customer.layby_outstanding)} />
                <Kpi label="First purchase" value={formatDate(customer.first_purchase)} />
                <Kpi label="Last purchase" value={formatDate(customer.last_purchase)} />
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Spend by month</h2>
                </div>
                <div className="card-body">
                  <Panel isLoading={false} error={null} isEmpty={series.length === 0}>
                    <div className="chart">
                      <ResponsiveContainer>
                        <BarChart data={series} margin={{ top: 6, right: 10, bottom: 0, left: 6 }}>
                          <CartesianGrid {...GRID} vertical={false} />
                          <XAxis dataKey="label" {...AXIS} minTickGap={20} />
                          <YAxis {...AXIS} width={62} tickFormatter={(value) => moneyCompact(value)} />
                          <Tooltip
                            content={<ChartTip format={(_, value) => money(value)} />}
                            cursor={{ fill: 'var(--page)' }}
                          />
                          <Bar dataKey="total" name="Spend" fill="var(--brand)" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="grid cols-2">
                <div className="card">
                  <div className="card-head">
                    <h2>Most bought</h2>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={customer.top_products.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th className="num">Revenue</th>
                              <th className="num">Units</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customer.top_products.map((row) => (
                              <tr key={`${row.sku ?? row.name}`}>
                                <td>
                                  {row.sku ? (
                                    <Link to={`/products/${encodeURIComponent(row.sku)}`}>{row.name}</Link>
                                  ) : (
                                    row.name
                                  )}
                                </td>
                                <td className="num">{money(row.revenue)}</td>
                                <td className="num">{count(row.units)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Contact</h2>
                  </div>
                  <div className="card-body">
                    <dl className="dl">
                      <dt>Phone</dt>
                      <dd>{customer.phone ?? '—'}</dd>
                      <dt>Email</dt>
                      <dd>{customer.email ?? '—'}</dd>
                      <dt>HIKE customer</dt>
                      <dd>{customer.hike_customer_id ?? '—'}</dd>
                      <dt>Linked profiles</dt>
                      <dd>{count(customer.linked_profiles)}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Purchase history</h2>
                  <span className="hint">{count(customer.purchases_total)} invoices</span>
                </div>
                <div className="card-body flush">
                  <Panel isLoading={false} error={null} isEmpty={customer.purchases.length === 0}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th className="num">Date</th>
                            <th>Status</th>
                            <th className="num">Items</th>
                            <th className="num">Total</th>
                            <th className="num">Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customer.purchases.map((row) => (
                            <tr key={row.invoice_id}>
                              <td>
                                <Link to={`/invoices/${row.invoice_id}`}>
                                  {row.hike_invoice_number ?? row.reference}
                                </Link>
                              </td>
                              <td className="num cell-sub">{formatDate(row.date)}</td>
                              <td>
                                <StatusChip status={row.status} />
                              </td>
                              <td className="num">{count(row.item_count)}</td>
                              <td className="num strong">{money(row.grand_total)}</td>
                              <td className="num">{money(row.total_paid)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <Pager
                      offset={offset}
                      limit={limit}
                      total={customer.purchases_total}
                      onChange={setOffset}
                    />
                  </Panel>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
